import {css, cx as addStyles} from '@emotion/css';
import {darken, lighten} from 'polished';
import React, {AnchorHTMLAttributes, ButtonHTMLAttributes, HTMLAttributes} from 'react';
import closeButton from '../Assets/images/closeButton.svg';
import {theme} from './Common';
import {Loading} from './Loading';

type ButtonStyles = 'primary' | 'secondary' | 'close' | 'addNotes' | 'blue';

type TagAttributes<T extends React.ElementType> = {
  0: ButtonHTMLAttributes<HTMLButtonElement>;
  1: AnchorHTMLAttributes<HTMLAnchorElement>;
}[T extends 'button' | undefined ? 0 : 1];

type ButtonElement = 'button' | 'a';

export type ButtonProps<T extends ButtonElement = 'button'> = {
  buttonStyle?: ButtonStyles;
  loading?: boolean;
  minimal?: boolean;
  icon?: JSX.Element;
  as?: T;
} & TagAttributes<T>;

export const Button = <T extends ButtonElement>({
  buttonStyle = 'primary',
  loading,
  minimal,
  icon,
  as,
  className,
  children,
  ...rest
}: ButtonProps<T>) =>
  React.createElement(
    as || 'button',
    {
      className: addStyles(
        flexCenter,
        textStyle,
        sizeRelatedStyle,
        outlineRelatedStyle,
        disabledStyle,
        css({
          transition: 'background 0.1s ease-in-out',
          cursor: 'pointer',
        }),
        {[primaryStyle]: buttonStyle === 'primary'},
        {[secondaryStyle]: buttonStyle === 'secondary'},
        {[closeStyle]: buttonStyle === 'close'},
        {[addNotesStyle]: buttonStyle === 'addNotes'},
        {[blueStyle]: buttonStyle === 'blue'},
        {[minimalStyle]: !!minimal},
        {[loadingStyle]: !!loading},
        {[hasIconStyle]: !!icon},
        className,
      ),
      ...rest,
    },
    loading ? (
      <Loading width={32} height={32} type='spinningBubbles' color={theme.colors.darkBlue} />
    ) : (
      <ButtonSubContainer>
        {buttonStyle === 'addNotes' && <AddText>+</AddText>}
        <ComplementaryText>
          {icon}
          {buttonStyle === 'close' ? '' : children}
        </ComplementaryText>
      </ButtonSubContainer>
    ),
  );
const ButtonSubContainer = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={css({
      display: 'flex',
      justifyContent: 'center',
      wordWrap: 'break-word',
      width: '300px',
    })}
    {...args}
  />
);
const ComplementaryText = (args: HTMLAttributes<HTMLDivElement>) => (
  <div
    className={addStyles(
      css({
        color: 'inherit',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        fontWeight: 'inherit',
        textAlign: 'inherit',
      }),
      flexCenter,
    )}
    {...args}
  />
);
const AddText = (args: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={css({fontSize: theme.font.sizes.medium, marginRight: theme.spacings.xxsmall})}
    {...args}
  />
);
const flexCenter = css({display: 'flex', alignItems: 'center', justifyContent: 'center'});
const textStyle = css({
  color: theme.colors.darkBlue,
  fontFamily: theme.font.family,
  fontWeight: theme.font.medium,
  fontSize: theme.font.sizes.small,
  textAlign: 'center',
  textDecoration: 'none',
});
const sizeRelatedStyle = css({width: '194px', height: '49px'});
const outlineRelatedStyle = css({borderRadius: '24px', outline: 'none'});
const disabledStyle = css({'&:disabled': {opacity: 0.5, cursor: 'not-allowed'}});
const loadingStyle = css({opacity: 0.5, cursor: 'not-allowed'});
const primaryStyle = css({
  background: theme.colors.orange,
  border: 0,
  '&:hover': {background: '#ff9d26'},
  '&:active': {
    background:
      'transparent linear-gradient(180deg, #eb8b17 0%, #ff9d26 100%) 0% 0% no-repeat padding-box',
  },
});
const secondaryStyle = css({
  background: theme.colors.white,
  border: `2px solid ${theme.colors.orange}`,
  '&:hover': {border: '3px solid #e67d00'},
  '&:active': {background: '#ffa23312', border: '3px solid #e67d00'},
});
const closeStyle = css({
  width: '52px',
  height: '52px',
  borderRadius: '50%',
  backgroundImage: `url(${closeButton})`,
  border: 0,
  outline: 'none',
});
const addNotesStyle = css({
  width: '100%',
  borderRadius: 0,
  justifyContent: 'flex-start',
  alignItems: 'center',
  border: 0,
  background: 'transparent',
  color: theme.colors.midGray,
  textAlign: 'left',
});
const blueStyle = css({
  height: 24,
  width: 72,
  borderRadius: 4,
  padding: `4px 8px`,
  background: theme.colors.darkBlue,
  border: 0,
  fontFamily: theme.font.family,
  fontWeight: theme.font.bold,
  fontSize: theme.font.sizes.xsmall,
  color: theme.colors.white,
});
const minimalStyle = css({
  background: 'none',
  color: `${lighten(0.1, theme.colors.darkBlue)}`,
  border: 0,
  '&:hover ': {background: 'none', color: `${darken(0.1, theme.colors.darkBlue)}`, border: 0},
});
const hasIconStyle = css({
  svg: {paddingRight: theme.spacings.xxsmall},
  [theme.mediaMaxWidth.medium]: {padding: `0 ${theme.spacings.small}`},
});
